import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';

import { api } from '../services/api/client';

export interface TenantInfo {
  id: number;
  name: string;
  slug: string;
  subdomain: string;
  settings: Record<string, unknown>;
}

interface TenantContextValue {
  tenant: TenantInfo | null;
  /** Always true in this single-tenant deployment: the backend pins every
   * request to the MDC organization regardless of hostname, so the SPA
   * never inspects window.location.host. Kept for consumers that were
   * written against the multi-tenant upstream. */
  isTenantSubdomain: boolean;
  loading: boolean;
}

const TenantContext = createContext<TenantContextValue>({
  tenant: null,
  isTenantSubdomain: true,
  loading: true,
});

export const TenantProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [tenant, setTenant] = useState<TenantInfo | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    api
      .get('/organizations/current/')
      .then((resp) => {
        if (alive) setTenant(resp.data?.tenant ?? null);
      })
      .catch(() => {
        // Network error — leave tenant=null so the SPA falls back to
        // generic branding. Never redirect anywhere: the domain this app is
        // served from is not the SPA's concern.
        if (alive) setTenant(null);
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, []);

  const value: TenantContextValue = useMemo(
    () => ({ tenant, isTenantSubdomain: true, loading }),
    [tenant, loading],
  );

  return <TenantContext.Provider value={value}>{children}</TenantContext.Provider>;
};

export const useTenant = (): TenantContextValue => useContext(TenantContext);
